import React, { useState } from 'react';
import { Download, ChevronLeft, ChevronRight, Search, FileSpreadsheet, FileJson, Copy, Check } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { NoTablesEmptyState } from './EmptyStates';

interface TableData {
  page: number;
  headers: string[];
  data: string[][];
}

interface TableViewerProps {
  tables: TableData[];
  documentName: string;
}

const TableViewer: React.FC<TableViewerProps> = ({ tables, documentName }) => {
  const [currentTableIndex, setCurrentTableIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);

  if (!tables || tables.length === 0) {
    return <NoTablesEmptyState />;
  }

  const currentTable = tables[currentTableIndex];
  
  // Filter table data based on search
  const filteredData = searchTerm 
    ? currentTable.data.filter(row =>
        row.some(cell => 
          cell?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : currentTable.data;

  const exportToCSV = () => {
    try {
      setExporting(true);
      // Export what is on screen. These used to dump every row while the
      // summary above reported the filtered count.
      const csvContent = [
        currentTable.headers.join(','),
        ...filteredData.map(row =>
          row.map(cell => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${documentName.replace(/\.[^/.]+$/, '')}_table_${currentTableIndex + 1}_page_${currentTable.page}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setTimeout(() => setExporting(false), 1000);
    } catch (error) {
      console.error('Failed to export CSV:', error);
      setExporting(false);
    }
  };

  const exportToJSON = () => {
    try {
      setExporting(true);
      // Convert table to array of objects
      const jsonData = filteredData.map(row => {
        const obj: Record<string, string> = {};
        currentTable.headers.forEach((header, index) => {
          obj[header] = row[index] || '';
        });
        return obj;
      });

      const jsonString = JSON.stringify({
        page: currentTable.page,
        headers: currentTable.headers,
        rows: jsonData,
        rowCount: filteredData.length,
        columnCount: currentTable.headers.length
      }, null, 2);

      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${documentName.replace(/\.[^/.]+$/, '')}_table_${currentTableIndex + 1}_page_${currentTable.page}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setTimeout(() => setExporting(false), 1000);
    } catch (error) {
      console.error('Failed to export JSON:', error);
      setExporting(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      // Create tab-separated format for easy pasting into Excel
      const rows = [currentTable.headers, ...currentTable.data];
      const tsvContent = rows.map(row => row.join('\t')).join('\n');

      await navigator.clipboard.writeText(tsvContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  return (
    <div className="p-6 space-y-4">
      {/* Header with navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-medium text-foreground">
            Table {currentTableIndex + 1} of {tables.length}
          </h3>
          <span className="text-sm text-foreground-muted">
            Page {currentTable.page} • {currentTable.data.length} rows × {currentTable.headers.length} columns
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {tables.length > 1 && (
            <>
              <button
                onClick={() => setCurrentTableIndex(Math.max(0, currentTableIndex - 1))}
                disabled={currentTableIndex === 0}
                className="p-2 text-foreground-muted hover:bg-coral-500/20 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="px-2 text-sm text-foreground-muted">
                {currentTableIndex + 1} / {tables.length}
              </span>
              <button
                onClick={() => setCurrentTableIndex(Math.min(tables.length - 1, currentTableIndex + 1))}
                disabled={currentTableIndex === tables.length - 1}
                className="p-2 text-foreground-muted hover:bg-coral-500/20 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
          <button
            onClick={copyToClipboard}
            className="flex items-center gap-2 px-3 py-1.5 bg-coral-500/10 hover:bg-coral-500/20 border border-coral-500/30 rounded-lg text-foreground-muted transition-colors text-sm"
            title="Copy to clipboard (Excel-ready)"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4" />
                <span className="hidden sm:inline">Copied!</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                <span className="hidden sm:inline">Copy</span>
              </>
            )}
          </button>
          <button
            onClick={exportToCSV}
            disabled={exporting}
            className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 hover:bg-green-500/20 border border-green-500/30 rounded-lg text-green-300 transition-colors text-sm disabled:opacity-50"
            title="Export to CSV"
          >
            <FileSpreadsheet className="w-4 h-4" />
            <span className="hidden sm:inline">CSV</span>
          </button>
          <button
            onClick={exportToJSON}
            disabled={exporting}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 rounded-lg text-blue-300 transition-colors text-sm disabled:opacity-50"
            title="Export to JSON"
          >
            <FileJson className="w-4 h-4" />
            <span className="hidden sm:inline">JSON</span>
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-foreground-muted w-5 h-5" />
        <input
          type="text"
          placeholder="Search in table..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground placeholder-coral-400/60 focus:outline-none focus:ring-2 focus:ring-coral-500/40"
        />
      </div>

      {/* Table display */}
      <div className="overflow-x-auto bg-black/20 rounded-lg">
        <table className="min-w-full divide-y divide-coral-500/20">
          <thead className="bg-coral-500/10">
            <tr>
              {currentTable.headers.map((header, idx) => (
                <th
                  key={idx}
                  className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider whitespace-nowrap"
                >
                  {header || `Column ${idx + 1}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-coral-500/10">
            {filteredData.length > 0 ? (
              filteredData.map((row, rowIdx) => (
                <tr key={rowIdx} className="hover:bg-coral-500/5 transition-colors">
                  {row.map((cell, cellIdx) => (
                    <td
                      key={cellIdx}
                      className="px-4 py-3 text-sm text-foreground"
                    >
                      {cell || '-'}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={currentTable.headers.length} className="px-4 py-8 text-center text-foreground-muted">
                  {searchTerm ? `No results found for "${searchTerm}"` : 'No data available'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Summary stats */}
      <Card className="bg-coral-500/5">
        <CardContent className="p-4">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-coral-400">Total Cells:</span>
              <span className="ml-2 text-foreground">
                {currentTable.headers.length * currentTable.data.length}
              </span>
            </div>
            <div>
              <span className="text-coral-400">Non-empty:</span>
              <span className="ml-2 text-foreground">
                {currentTable.data.reduce((acc, row) =>
                  acc + row.filter(cell => cell && cell.toString().trim()).length, 0
                )}
              </span>
            </div>
            <div>
              <span className="text-coral-400">Displayed:</span>
              <span className="ml-2 text-foreground">
                {filteredData.length} rows
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Tips */}
      <div className="glass-effect p-4 rounded-lg border-l-4 border-blue-500">
        <div className="flex items-start gap-3">
          <Download className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-blue-100 mb-1">Export Options</h4>
            <ul className="text-xs text-blue-300/70 space-y-1">
              <li>• <strong>Copy</strong> - Paste directly into Excel or Google Sheets (preserves formatting)</li>
              <li>• <strong>CSV</strong> - Import into QuickBooks, Xero, or any spreadsheet software</li>
              <li>• <strong>JSON</strong> - Use for API integrations or custom processing</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableViewer;